import React from "react";
import { HiTrendingUp, HiCurrencyDollar, HiUserGroup } from "react-icons/hi";
import { formatCurrency } from "../../../utils/formatCurrency";
import { useNetWorth } from "../hooks/useNetWorth";
import { useFamilyMetrics } from "../hooks/useFamilyMetrics";

interface WelcomeHeroProps {
  userName?: string | null;
  timeOfDay: string;
}

export const WelcomeHero: React.FC<WelcomeHeroProps> = ({
  userName,
  timeOfDay,
}) => {
  const { data: netWorthData } = useNetWorth();
  const { data: familyData } = useFamilyMetrics();

  const netWorth = netWorthData?.netWorth || 0;
  const previousNetWorth = netWorthData?.previousNetWorth || 0;
  const activeMemberCount = familyData?.activeMemberCount || 1;
  const totalAccounts = familyData?.totalAccounts || 0;

  const netWorthChange = netWorth - previousNetWorth;
  const netWorthTrend = (netWorthChange / previousNetWorth) * 100;

  return (
    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-purple-800 rounded-xl text-white p-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">
            Good {timeOfDay}, {userName || "there"}! 👋
          </h1>
          <p className="text-indigo-100 text-lg">
            Here&#39;s your family&#39;s financial snapshot
          </p>
        </div>

        <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-4 py-2">
          <HiUserGroup className="w-5 h-5" />
          <span>{activeMemberCount} family members</span>
          <span>•</span>
          <span>{totalAccounts} accounts</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <MetricCard
          title="Net Worth"
          value={formatCurrency(netWorth)}
          trend={netWorthTrend}
          icon={HiCurrencyDollar}
        />
        <MetricCard
          title="Monthly Change"
          value={formatCurrency(netWorthChange)}
          trend={netWorthChange > 0 ? netWorthTrend : -netWorthTrend}
          icon={HiTrendingUp}
        />
        <MetricCard
          title="Family Score"
          value="85"
          subtext="Financial Health"
          icon={HiUserGroup}
        />
      </div>
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: string;
  subtext?: string;
  trend?: number;
  icon: React.ComponentType<{ className?: string }>;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtext,
  trend,
  icon: Icon,
}) => (
  <div className="bg-white/10 rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-indigo-100">{title}</span>
      <Icon className="w-5 h-5 text-indigo-100" />
    </div>
    <div className="text-2xl font-bold mb-1">{value}</div>
    {trend !== undefined && (
      <div
        className={`text-sm ${trend >= 0 ? "text-green-300" : "text-red-300"}`}
      >
        {trend >= 0 ? "↑" : "↓"} {Math.abs(trend).toFixed(1)}%
      </div>
    )}
    {subtext && <div className="text-sm text-indigo-100">{subtext}</div>}
  </div>
);
